/**
 * Shared persona-grouped example chips for guide creation.
 * Used on the home page and the org guide creation flow (/org/guides/new).
 */

export interface ChipDef {
  label: string;
  /** Specific prompt for guide generation (richer than the label) */
  promptText: string;
  /** If set, matches against featured guides to open the drawer preview (home page only) */
  guideRef?: { orgSlug: string; slug: string };
}

export const PERSONA_COLORS: Record<
  string,
  { bg: string; text: string; border: string; accent: string }
> = {
  Government: { bg: '#FFF7ED', text: '#9A3412', border: '#FED7AA', accent: '#F97316' },
  'Client Work': { bg: '#EFF6FF', text: '#1E40AF', border: '#BFDBFE', accent: '#3B82F6' },
  'Teams & Ops': { bg: '#F0FDF4', text: '#166534', border: '#BBF7D0', accent: '#22C55E' },
  Education: { bg: '#FAF5FF', text: '#6B21A8', border: '#E9D5FF', accent: '#A855F7' },
};

export const EXAMPLE_CHIPS: { persona: string; icon: string; chips: ChipDef[] }[] = [
  {
    persona: 'Government',
    icon: '\u{1F3DB}\u{FE0F}', // 🏛️
    chips: [
      {
        label: 'FOIA request processing',
        promptText:
          'Create an end-to-end Freedom of Information Act request workflow from intake through search, review, redaction, and response',
      },
      {
        label: 'Federal grant management',
        promptText:
          'Build a federal grant lifecycle process from opportunity identification through application, award, compliance monitoring, and closeout',
      },
      {
        label: 'Public records request',
        promptText:
          'Create a public records request process from intake through search, legal review, redaction, and fulfillment',
      },
      {
        label: 'Permit application review',
        promptText:
          'Design a permit application workflow covering submission, completeness check, technical review, public comment, and issuance',
      },
    ],
  },
  {
    persona: 'Client Work',
    icon: '\u{1F91D}', // 🤝
    chips: [
      {
        label: 'Client onboarding portal',
        guideRef: { orgSlug: 'planapus', slug: 'client-onboarding' },
        promptText:
          'Create a client onboarding process from initial welcome through project setup, kickoff meeting, and handoff to the delivery team',
      },
      {
        label: 'Vendor qualification portal',
        guideRef: { orgSlug: 'planapus', slug: 'vendor-qualification' },
        promptText:
          'Build a vendor qualification workflow covering identification, due diligence, structured evaluation, and formal approval',
      },
      {
        label: 'Project kickoff portal',
        guideRef: { orgSlug: 'planapus', slug: 'project-kickoff' },
        promptText:
          'Design a structured project kickoff process including pre-work, stakeholder alignment meeting, post-kickoff action items, and first sprint planning',
      },
      {
        label: 'Contractor setup portal',
        guideRef: { orgSlug: 'planapus', slug: 'contractor-setup' },
        promptText:
          'Set up a new contractor onboarding process with documentation collection, system access provisioning, orientation, and first-week milestones',
      },
    ],
  },
  {
    persona: 'Teams & Ops',
    icon: '\u{2699}\u{FE0F}', // ⚙️
    chips: [
      {
        label: 'Monthly close portal',
        promptText:
          'Design a month-end financial close process with pre-close prep, account reconciliations, journal entry adjustments, and reporting deadlines',
      },
      {
        label: 'Incident response portal',
        promptText:
          'Create an incident response runbook from detection and triage through containment, resolution, post-mortem, and follow-up action tracking',
      },
      {
        label: 'Employee onboarding portal',
        promptText:
          'Build a comprehensive new employee onboarding process covering IT setup, HR paperwork, team introductions, training schedule, and 30-60-90 day milestones',
      },
      {
        label: 'Sprint planning portal',
        promptText:
          'Design a sprint planning workflow from backlog grooming through capacity planning, sprint goal setting, task breakdown, and commitment review',
      },
    ],
  },
  {
    persona: 'Education',
    icon: '\u{1F393}', // 🎓
    chips: [
      {
        label: 'Course setup portal',
        promptText:
          'Build a course setup process from curriculum design through syllabus creation, LMS configuration, resource upload, and pre-semester checklist',
      },
      {
        label: 'Student application portal',
        promptText:
          'Create a student application review process from submission receipt through document verification, committee review, decision notification, and enrollment',
      },
      {
        label: 'Grant submission portal',
        promptText:
          'Design a grant submission workflow covering opportunity identification, proposal drafting, budget preparation, internal review, and submission tracking',
      },
      {
        label: 'Classroom launch portal',
        promptText:
          'Build a classroom launch checklist from facility setup through technology testing, material distribution, student orientation, and first-week activities',
      },
    ],
  },
];
